import { nanoid } from 'nanoid';
import { forEachObjIndexed, omit, sum } from 'ramda';
import { uniqueWith } from 'remeda';
import type { Location, NodeEntry, NodeMatch } from 'slate';
import { Editor, Node, Path, Point, Range, Transforms } from 'slate';

import {
  getCumulativeEdges,
  getFrontLoadedCounts,
  getSpansBetweenEdges,
  getWidthsBetweenEdges,
} from './table-distribution';
import type { Length } from '../../types';
import { arrayEntries, isPresent, pxToIn, pxToTwips, zip } from '../../util';
import type {
  InsertTableColumnLocation,
  InsertTableRowLocation,
  ParagraphElement,
  PartialTableBorders,
  RemoveTableCellEdit,
  RemoveTableRowEdit,
  SuggestedTableCellEdit,
  SuggestedTableRowEdit,
  SuggestionSource,
  TableBorder,
  TableBorders,
  TableCellElement,
  TableCellProps,
  TableElement,
  TableGridRect,
  TableGridSlot,
  TableProps,
  TableRowCellRange,
  TableRowElement,
  TextProps,
} from '../internal';
import {
  AlineTransforms,
  applySuggestions,
  createDefaultParagraph,
  createDefaultTableCellElement,
  createDefaultTableElement,
  createDefaultTableRowElement,
  createDefaultText,
  EditingMode,
  generateLocationId,
  generateSuggestedEditGroupId,
  isInsertTableCellEdit,
  isParagraphElement,
  isRenderModelTable,
  isTableCellElement,
  isTableElement,
  isTableEntry,
  isTableRowElement,
  Queries,
} from '../internal';

interface NewTableProps {
  columnCount: number;
  rowCount: number;
  tableBorders?: TableBorders;
}

interface RowInsertionIndices {
  /**
   * Cell index of a horizontally merged cell that contains the insertion column within its span.
   * When set, the cell's columnSpan should be increased instead of inserting new cells.
   */
  horizontallMergedCellIndex: number | null;
  /**
   * Cell index in the row where new cells should be inserted. Defaults to the end of the row.
   */
  insertionCellIndex: number;
  /**
   * Cell index that covers the source column, used as a template for styling.
   * Null if no cell in the row covers the source column.
   */
  sourceCellIndex: number | null;
}

function findRowInsertionIndices(
  editor: Editor,
  [row, rowPath]: NodeEntry<TableRowElement>,
  insertionIndex: number,
  sourceIndex: number
): RowInsertionIndices {
  let currentColumn = 0;
  let insertionCellIndex = row.children.length;
  let horizontallyMergedCellIndex: number | null = null;
  let sourceCellIndex: number | null = null;

  for (const [cellIndex, cell] of arrayEntries(row.children)) {
    const cellEndColumn = Queries.getTableCellEndColumn(editor, [cell, [...rowPath, cellIndex]]);
    if (sourceCellIndex === null && sourceIndex >= currentColumn && sourceIndex <= cellEndColumn)
      sourceCellIndex = cellIndex;

    if (insertionIndex <= currentColumn) {
      insertionCellIndex = cellIndex;
      break;
    }

    if (insertionIndex > currentColumn && insertionIndex <= cellEndColumn) {
      horizontallyMergedCellIndex = cellIndex;
      break;
    }

    currentColumn = cellEndColumn + 1;
  }

  return {
    horizontallMergedCellIndex: horizontallyMergedCellIndex,
    insertionCellIndex,
    sourceCellIndex,
  };
}

interface InsertedCellOptions {
  authorId: string;
  editingMode: EditingMode;
  groupId?: string;
}

interface TemplateCellResult {
  cellProps: Partial<TableCellProps>;
  paraProps: Partial<ParagraphElement> | undefined;
  textProps: Partial<TextProps> | undefined;
}

function getTemplateCellProps(
  templateCell: TableCellElement,
  rowPath: Path,
  templateCellIndex: number,
  options: { copyVerticalMerge: boolean }
): TemplateCellResult {
  const firstParagraphEntry = Queries.getParagraphEntryInRoot<ParagraphElement>([
    templateCell,
    [...rowPath, templateCellIndex],
  ]);

  let paraProps: Partial<ParagraphElement> | undefined = undefined;
  let textProps: Partial<TextProps> | undefined = undefined;
  if (firstParagraphEntry) {
    const [paragraph] = firstParagraphEntry;

    const { children, suggestedEdits, ...restParagraphProps } = paragraph;
    paraProps = restParagraphProps;

    const [firstText] = Node.texts(paragraph).next().value ?? [];
    textProps = firstText ? Queries.getActiveTextProps(firstText) : undefined;
  }

  const renderedCell = applySuggestions(templateCell);
  const cellProps: Partial<TableCellProps> = {};
  if (renderedCell.borders) cellProps.borders = renderedCell.borders;
  if (renderedCell.margins) cellProps.margins = renderedCell.margins;
  if (renderedCell.shade) cellProps.shade = renderedCell.shade;
  if (options.copyVerticalMerge && renderedCell.verticalMerge)
    cellProps.verticalMerge = renderedCell.verticalMerge;

  return { cellProps, paraProps, textProps };
}

function createInsertedCells(
  count: number,
  width: TableCellElement['width'],
  template: TemplateCellResult,
  options: InsertedCellOptions
): TableCellElement[] {
  return Array.from({ length: count }, () => createInsertedCell(width, template, options));
}

function createInsertedCell(
  width: TableCellElement['width'],
  template: TemplateCellResult,
  options: InsertedCellOptions
): TableCellElement {
  const { authorId, editingMode, groupId } = options;
  const suggestedEdits: SuggestedTableCellEdit[] = [];
  if (editingMode === EditingMode.SUGGESTING) {
    suggestedEdits.push({
      authorId,
      groupId,
      type: 'insertTableCell',
      value: {},
    });
  }

  return {
    ...createDefaultTableCellElement({
      paraProps: template.paraProps,
      textProps: template.textProps,
      width,
    }),
    ...template.cellProps,
    suggestedEdits,
  };
}

/** One of the cells a split divides a region into. */
interface RegionPart {
  columnSpan: number;
  width: Length.Twip;
}

/** A physical cell of a region, at the grid position it is first met. */
interface RegionCell {
  columnIndex: number;
  isVerticalMergeContinuation: boolean;
  path: Path;
  rowIndex: number;
}

/** What a merge of the grid selection moves, keeps and removes. */
interface MergePlan {
  /** Cells whose content moves into the merged cell, in reading order. */
  contentSourcePaths: Path[];
  /** Cells below the merged cell in its first column, which stay as its merge ghosts. */
  continuationPaths: Path[];
  /** The region's top left cell, which survives the merge. */
  mergedCellPath: Path;
  /** Props shared by the merged cell and its ghosts. */
  mergedCellProps: Partial<TableCellProps>;
  rect: TableGridRect;
  /** Cells the merged cell absorbs. */
  removalPaths: Path[];
  tablePath: Path;
}

const TableTransforms = {
  /**
   * Deletes the table containing the selection, taking into account editing mode.
   */
  deleteTable: (
    editor: Editor,
    options: {
      authorId: string;
      editingMode: EditingMode;
      at?: Location;
    }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { at = editor.selection, authorId, editingMode } = options;
      if (!at) return;

      const tableEntry = Queries.atOrAbove(editor, { at, match: isTableElement });
      if (!tableEntry) return;

      const [table, tablePath] = tableEntry;

      if (editingMode === EditingMode.SUGGESTING) {
        const removeRowEdit: RemoveTableRowEdit = {
          authorId,
          createdAt: new Date().toISOString(),
          groupId: nanoid(),
          type: 'removeTableRow',
          value: {},
        };

        for (const [rowIndex, row] of arrayEntries(table.children)) {
          const newSuggestedEdits = Queries.newEdits(row, removeRowEdit);
          if (newSuggestedEdits) {
            Transforms.setNodes(
              editor,
              { suggestedEdits: newSuggestedEdits },
              { at: [...tablePath, rowIndex] }
            );
          }
        }
        return;
      }

      Transforms.removeNodes(editor, { at: tablePath });

      // Move cursor to after the deleted table (tablePath is now the path after the the deleted table).
      if (Node.has(editor, tablePath))
        AlineTransforms.select(editor, Editor.start(editor, tablePath));
    });
  },

  /**
   * Deletes table columns which intersect the selection, taking into account editing mode.
   */
  deleteTableColumns: (
    editor: Editor,
    options: {
      authorId: string;
      editingMode: EditingMode;
      at?: Location;
      /**
       * Overrides the generated group id — pass the server-generated groupId
       * when the deletion is an AI suggestion so the chat card, marks, and
       * suggestion markers share one identifier.
       */
      groupId?: string;
      /**
       * Present when the columns are removed by an AI chat suggestion; stamped
       * onto the cell and content marks so source-based accept/reject resolves
       * the whole group.
       */
      source?: SuggestionSource;
    }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { at = editor.selection, authorId, editingMode, source } = options;
      if (!at) return;

      const tableEntry = Editor.above(editor, { at, match: isTableElement });
      if (!tableEntry) return;
      const [, tablePath] = tableEntry;

      const selectedCellsInfo = Queries.getTableCellsInSelectedColumns(editor, { at });
      if (!selectedCellsInfo) return;
      const { endColumnIndex, startColumnIndex, tableCellEntries } = selectedCellsInfo;

      if (editingMode === EditingMode.SUGGESTING) {
        const groupId = options.groupId ?? nanoid();
        const removeCellEdit: RemoveTableCellEdit = {
          authorId,
          createdAt: new Date().toISOString(),
          groupId,
          source,
          type: 'removeTableCell',
          value: {},
        };

        for (const [tableCell, tableCellPath] of tableCellEntries.toReversed()) {
          const newSuggestedEdits = Queries.newEdits(tableCell, removeCellEdit);
          if (newSuggestedEdits) {
            Transforms.setNodes(
              editor,
              { suggestedEdits: newSuggestedEdits },
              { at: tableCellPath }
            );
          }

          AlineTransforms.suggestDeletionOfDescendants(editor, {
            at: tableCellPath,
            authorId,
            groupId,
            source,
          });
        }
        return;
      }

      /**
       * The render model is only needed for the direct-edit width math below,
       * and only client editors carry one — keeping this lookup out of the
       * suggesting path lets server-side (AI suggestion) editors delete
       * columns too.
       */
      const [rMTable] = Queries.renderModelNodeFromCMLocation(editor, tablePath);
      if (!isRenderModelTable(rMTable)) throw new Error('Expected render model table');
      const { columnWidthsPx } = rMTable;

      for (const [, tableCellPath] of tableCellEntries.toReversed()) {
        Transforms.removeNodes(editor, { at: tableCellPath });
      }

      const previousTotalWidthPx = sum(columnWidthsPx);
      const deletedColumnsWidthPx = sum(columnWidthsPx.slice(startColumnIndex, endColumnIndex + 1));
      const newTableWidth = Math.round(pxToTwips(previousTotalWidthPx - deletedColumnsWidthPx));

      AlineTransforms.setTableProps(editor, {
        at: tablePath,
        authorId,
        editingMode,
        props: { width: { type: 'tw', value: newTableWidth } },
      });

      AlineTransforms.normalizeTableWidths(editor, {
        authorId,
        editingMode,
        path: tablePath,
      });

      // Select the top most cell after the deleted columns.
      const firstCellEntry = tableCellEntries.at(0);
      if (firstCellEntry && Node.has(editor, firstCellEntry[1])) {
        AlineTransforms.select(editor, Editor.start(editor, firstCellEntry[1]));
      }
    });
  },

  /**
   * Deletes content in a table that is selected via a Range.
   *
   * @returns what is left of the range for the caller to delete, collapsed when the removal used it
   * all up, or null when nothing is left of the document around it.
   */
  deleteTableContent: (
    editor: Editor,
    options: { range: Range; cellEntry?: NodeEntry<TableCellElement> }
  ): Range | null => {
    return AlineTransforms.withoutNormalizing<Range | null>(editor, () => {
      const { cellEntry, range } = options;
      const [start, end] = Range.edges(range);
      const tableEntries = uniqueWith(
        [start, end]
          .map(at => Editor.above(editor, { at, match: isTableElement }))
          .filter(isPresent),
        ([, pathA], [, pathB]) => Path.equals(pathA, pathB)
      );

      /**
       * Find all rows and convert them to array first.
       * Because when using generator Editor.nodes with reverse:true it will be causing issues
       * in the loop below, because the generator will try to descend into children after
       * Transforms.removeNodes has already deleted that row ()
       */
      const rowEntries = Editor.nodes(editor, { at: range, match: isTableRowElement }).toArray();

      const removedPaths = tableEntries.flatMap(([table, tablePath]) => {
        const rowPaths = rowEntries
          .map(([, rowPath]) => rowPath)
          .filter(
            rowPath =>
              Path.isChild(rowPath, tablePath) &&
              // Within a cell, only the rows of tables nested inside it
              (!cellEntry || Path.isDescendant(rowPath, cellEntry[1]))
          );

        return rowPaths.length === table.children.length ? [tablePath] : rowPaths;
      });

      // Each end steps out of the removed nodes towards the other.
      const startOutside = getPointOutsideNodes(editor, start, { paths: removedPaths });
      const endOutside = getPointOutsideNodes(editor, end, { paths: removedPaths, reverse: true });
      const isRangeUsedUp = !startOutside || !endOutside || Point.isAfter(startOutside, endOutside);

      const leftoverStart = startOutside ?? endOutside;
      if (!leftoverStart) return null;

      const leftoverEnd = isRangeUsedUp ? leftoverStart : endOutside;

      const startRef = Editor.pointRef(editor, leftoverStart);
      const endRef = Editor.pointRef(editor, leftoverEnd);

      for (const removedPath of removedPaths.toSorted(Path.compare).toReversed())
        Transforms.removeNodes(editor, { at: removedPath });

      const [anchor, focus] = Range.isBackward(range)
        ? [endRef.unref(), startRef.unref()]
        : [startRef.unref(), endRef.unref()];

      return anchor && focus ? { anchor, focus } : null;
    });
  },

  /**
   * Deletes table rows which intersect the selection, taking into account editing mode.
   */
  deleteTableRows: (
    editor: Editor,
    options: {
      authorId: string;
      editingMode: EditingMode;
      at?: Location;
      /**
       * Overrides the generated group id — pass the server-generated groupId
       * when the deletion is an AI suggestion so the chat card, marks, and
       * suggestion markers share one identifier.
       */
      groupId?: string;
      /**
       * Present when the rows are removed by an AI chat suggestion; stamped
       * onto the row and content marks so source-based accept/reject resolves
       * the whole group.
       */
      source?: SuggestionSource;
    }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { at = editor.selection, authorId, editingMode, source } = options;
      if (!at) return;

      const { rowEntries } = Queries.getContiguousTableRows(editor, { at });
      if (!rowEntries.length) return;

      const [[, firstPath]] = rowEntries;
      const prevParagraphEntry = Editor.previous(editor, {
        at: firstPath,
        match: isParagraphElement,
      });

      if (editingMode === EditingMode.SUGGESTING) {
        const groupId = options.groupId ?? nanoid();
        const removeRowEdit: RemoveTableRowEdit = {
          authorId,
          createdAt: new Date().toISOString(),
          groupId,
          source,
          type: 'removeTableRow',
          value: {},
        };

        rowEntries.forEach(([row, deletePath]) => {
          const newSuggestedEdits = Queries.newEdits(row, removeRowEdit);
          if (newSuggestedEdits) {
            Transforms.setNodes(editor, { suggestedEdits: newSuggestedEdits }, { at: deletePath });
          }

          AlineTransforms.suggestDeletionOfDescendants(editor, {
            at: deletePath,
            authorId,
            groupId,
            source,
          });
        });
        return;
      }
      // Collect the paths to delete in reverse so that they don't change mid-deletion
      const rowDeletionPaths = rowEntries.map(([, rowPath]) => rowPath).reverse();
      rowDeletionPaths.forEach(deletePath => Transforms.removeNodes(editor, { at: deletePath }));

      if (prevParagraphEntry) {
        const [, prevParagraphPath] = prevParagraphEntry;
        AlineTransforms.select(editor, Editor.end(editor, prevParagraphPath));
      }
    });
  },

  /**
   * Resize a table so that it exactly fits inside its parent container.
   */
  fitTableToContainer: (
    editor: Editor,
    options: {
      authorId: string;
      editingMode: EditingMode;
      at?: Location;
    }
  ): void => {
    const { at = editor.selection, authorId, editingMode } = options;
    if (!at) return;

    const tableEntry = Queries.atOrAbove(editor, { at, match: isTableElement });
    if (!tableEntry) return;
    const [table, tablePath] = tableEntry;

    const leftBorderWidth = Queries.getTableMaximumBorderWidth(table, 'left', 'tw');
    const rightBorderWidth = Queries.getTableMaximumBorderWidth(table, 'right', 'tw');

    const containerContentWidth = pxToTwips(
      Queries.getContainerContentWidthPx(editor, { at: tablePath })
    );

    const width = containerContentWidth - (leftBorderWidth + rightBorderWidth) / 2;

    AlineTransforms.setTableProps(editor, {
      at: tablePath,
      authorId,
      editingMode,
      props: {
        indent: { type: 'nil' },
        width: { type: 'tw', value: width },
      },
    });
  },

  /**
   * Inserts a table at the provided Range, taking into account editing mode.
   *
   * TODO: If changing this method, consider adding calls to AlineTransforms.announce to improve
   * screen reader support as per EDI-397.
   */
  insertTable: (
    editor: Editor,
    options: {
      authorId: string;
      editingMode: EditingMode;
      newTableProps: NewTableProps;
      at?: Location;
    }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { at = editor.selection, authorId, editingMode, newTableProps } = options;
      if (!at) return;

      const suggestionGroupId =
        editingMode === EditingMode.SUGGESTING ? generateSuggestedEditGroupId() : undefined;

      AlineTransforms.insertContentNodes(editor, {
        at,
        authorId,
        editingMode,
        nodes: [
          createDefaultTableElement({
            ...newTableProps,
            authorId,
            contentWidthIn: pxToIn(
              // TODO: No need to use Editor.parent once getRMPathsFromCMPath supports text nodes
              // https://linear.app/aline/issue/EDI-251/migrate-from-relativepath-to-relativeindex
              Queries.getContainerContentWidthPx(editor, { at: Editor.parent(editor, at)[1] })
            ),
            editingMode,
            groupId: suggestionGroupId,
          }),
        ],
        processEdits: false,
        select: 'start',
        suggestionGroupId,
      });
    });
  },

  /**
   * Inserts table columns to the right or left of selected table cells from
   * a single table. Takes into account vertically and horizontally merged cells,
   * cell properties, first paragraph properties in cell
   */
  insertTableColumns: (
    editor: Editor,
    options: {
      authorId: string;
      columnLocation: InsertTableColumnLocation;
      editingMode: EditingMode;
      at?: Location;
      numInsertedCols?: number;
      select?: boolean;
    }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const {
        at = editor.selection,
        authorId,
        columnLocation,
        editingMode,
        numInsertedCols,
        select = true,
      } = options;

      if (!at) return;

      const groupId = editingMode === EditingMode.SUGGESTING ? nanoid() : undefined;

      const initialTableEntry = Editor.above(editor, { at, match: isTableElement });
      if (!initialTableEntry) return;

      AlineTransforms.normalizeTableWidths(editor, {
        authorId,
        editingMode,
        groupId,
        path: initialTableEntry[1],
      });

      const tableEntry = Editor.above(editor, { at, match: isTableElement });
      if (!tableEntry) return;

      const [table, tablePath] = tableEntry;

      const cellEntries = Queries.getContiguousTableCells(editor, { at });
      if (!cellEntries.length) return;

      const [firstCellEntry] = cellEntries;
      const lastCellEntry = cellEntries[cellEntries.length - 1];
      if (!firstCellEntry || !lastCellEntry) return;

      const startColumnIndex = Queries.getTableCellStartColumn(editor, firstCellEntry);
      const endColumnIndex = Queries.getTableCellEndColumn(editor, lastCellEntry);
      const insertedColumnsCount = numInsertedCols ?? cellEntries.length;
      if (insertedColumnsCount < 1) return;

      const sourceColumnIndex = columnLocation === 'left' ? startColumnIndex : endColumnIndex;
      const insertionColumnIndex =
        columnLocation === 'left' ? startColumnIndex : endColumnIndex + 1;

      // gridColumns should be always present and correct after width normalization
      const { gridColumns } = applySuggestions(table);
      if (!gridColumns) throw new Error('Expected gridColumns');

      const insertedCellWidth = gridColumns.at(sourceColumnIndex);
      if (insertedCellWidth === undefined)
        throw new Error('Expected gridColumns to include sourceColumnIndex');

      let firstSelectionPath: Path | null = null;

      for (const [rowIndex, row] of arrayEntries(table.children)) {
        if (!row.children.length) continue;

        const rowPath = [...tablePath, rowIndex];

        const { horizontallMergedCellIndex, insertionCellIndex, sourceCellIndex } =
          findRowInsertionIndices(editor, [row, rowPath], insertionColumnIndex, sourceColumnIndex);

        if (horizontallMergedCellIndex !== null) {
          const horizontallyMergedCellPath = [...rowPath, horizontallMergedCellIndex];
          const horizontallyMergedCell = row.children[horizontallMergedCellIndex];
          if (!horizontallyMergedCell) continue;
          const currentColumnSpan = Queries.renderedTableCellColumnSpan(horizontallyMergedCell);

          AlineTransforms.setTableCellProps(editor, {
            at: horizontallyMergedCellPath,
            authorId,
            editingMode,
            groupId,
            props: {
              columnSpan: currentColumnSpan + insertedColumnsCount,
            },
          });

          if (!firstSelectionPath) firstSelectionPath = horizontallyMergedCellPath;
          continue;
        }

        const fallbackTemplateCellIndex =
          columnLocation === 'left'
            ? Math.min(insertionCellIndex, row.children.length - 1)
            : Math.max(0, insertionCellIndex - 1);

        const templateCellIndex = sourceCellIndex ?? fallbackTemplateCellIndex;
        const templateCell = row.children[templateCellIndex];
        if (!templateCell) continue;

        const template = getTemplateCellProps(templateCell, rowPath, templateCellIndex, {
          copyVerticalMerge: sourceCellIndex !== null,
        });

        const insertedCells = createInsertedCells(
          insertedColumnsCount,
          insertedCellWidth,
          template,
          { authorId, editingMode, groupId }
        );

        const insertionPath = [...rowPath, insertionCellIndex];
        Transforms.insertNodes(editor, insertedCells, { at: insertionPath });

        if (!firstSelectionPath) firstSelectionPath = insertionPath;
      }

      /**
       * Set the table's preferred width to auto. This ensures that for autofit tables, the table
       * will not exceed the width of its container.
       */
      AlineTransforms.setTableProps(editor, {
        at: tablePath,
        authorId,
        editingMode,
        groupId,
        props: { width: { type: 'auto' } },
      });

      AlineTransforms.normalizeTableWidths(editor, {
        authorId,
        editingMode,
        groupId,
        path: tablePath,
      });

      if (select && firstSelectionPath && Node.has(editor, firstSelectionPath))
        AlineTransforms.select(editor, Editor.start(editor, firstSelectionPath));
    });
  },

  /**
   * Inserts table rows above or below the row(s) of the cursor, taking into account editing mode.
   */
  insertTableRows: (
    editor: Editor,
    options: {
      authorId: string;
      editingMode: EditingMode;
      rowLocation: InsertTableRowLocation;
      at?: Location;
      numInsertedRows?: number;
      select?: boolean;
    }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const {
        at = editor.selection,
        authorId,
        editingMode,
        numInsertedRows,
        rowLocation,
        select = true,
      } = options;
      if (!at) return;

      const { rowEntries, tableEntry } = Queries.getContiguousTableRows(editor, { at });
      if (!tableEntry || !rowEntries.length) return;

      const lastRowEntry = rowEntries[rowEntries.length - 1];
      const [firstRowEntry] = rowEntries;
      const [row, rowPath] = rowLocation === 'above' ? firstRowEntry : lastRowEntry;
      const cellDefinitions = row.children.map((cell, index) => {
        const cellPath = [...rowPath, index];
        const firstParagraphEntry = Queries.getParagraphEntryInRoot<ParagraphElement>([
          cell,
          cellPath,
        ]);
        if (!firstParagraphEntry)
          return {
            inline: createDefaultText(),
            paragraph: createDefaultParagraph(),
            width: cell.width,
          };

        const [paragraph] = firstParagraphEntry;
        const [inline] = paragraph.children;
        return { inline, paragraph, width: cell.width };
      });
      const columnCount = row.children.length;
      const insertionPath = rowLocation === 'above' ? rowPath : Path.next(rowPath);
      const suggestedEdits: SuggestedTableRowEdit[] = [];

      if (editingMode === EditingMode.SUGGESTING) {
        suggestedEdits.push({
          authorId,
          groupId: nanoid(),
          type: 'insertTableRow',
          value: {},
        });
      }

      /**
       * TODO: Respect existing formatting
       * @url https://arity.atlassian.net/browse/AL-1900
       */
      const insertedRows: TableRowElement[] = Array.from(
        { length: numInsertedRows ?? rowEntries.length },
        () => ({
          ...createDefaultTableRowElement({ columnCount }),
          children: cellDefinitions.map(({ inline, paragraph, width }) => ({
            ...createDefaultTableCellElement({ width }),
            children: [
              {
                ...omit(['listLevel', 'listProps'], paragraph),
                children: [{ ...inline, comments: [], suggestedEdits: [], text: '' }],
                // Fresh id per cell; the spread above would otherwise duplicate the template row's locationId.
                locationId: generateLocationId(),
                suggestedEdits: [],
              },
            ],
          })),
          suggestedEdits,
        })
      );

      Transforms.insertNodes(editor, insertedRows, { at: insertionPath });

      if (select) AlineTransforms.select(editor, Editor.start(editor, insertionPath));
    });
  },

  /**
   * Pad the rows of a ragged table with plain cells, so every row reaches its last column. Deleting
   * a row or column that held a merged cell can leave such holes, and nothing else fills them.
   */
  makeTableRectangular: (
    editor: Editor,
    options: { authorId: string; editingMode: EditingMode; at?: Location }
  ): void => {
    const { at = editor.selection, authorId, editingMode } = options;

    if (editingMode === EditingMode.SUGGESTING) return;
    if (!at) return;

    const tableEntry = Editor.above(editor, { at, match: isTableElement });
    if (!tableEntry) return;

    const [table, tablePath] = tableEntry;
    if (Queries.getIsTableRectangular(table)) return;

    const columnCount = Queries.getTableColumnCount(table);

    AlineTransforms.withoutNormalizing(editor, () => {
      for (const [rowIndex, tableRow] of arrayEntries(table.children)) {
        const paddingCount = columnCount - Queries.getTableRowColumnCount(tableRow);
        const templateCellIndex = tableRow.children.length - 1;
        const templateCell = tableRow.children.at(templateCellIndex);
        if (paddingCount < 1 || !templateCell) continue;

        const rowPath = [...tablePath, rowIndex];
        const template = getTemplateCellProps(templateCell, rowPath, templateCellIndex, {
          copyVerticalMerge: false,
        });

        Transforms.insertNodes(
          editor,
          createInsertedCells(paddingCount, undefined, template, { authorId, editingMode }),
          { at: [...rowPath, tableRow.children.length] }
        );
      }

      // Sizes the padding cells by the grid columns they fill.
      AlineTransforms.normalizeTableWidths(editor, { authorId, editingMode, path: tablePath });
    });
  },

  /** Merge the grid-selected cells into the top left one, which takes their content and space. */
  mergeTableCells: (editor: Editor, options: { editingMode: EditingMode }): void => {
    const { editingMode } = options;

    // No suggested edit type represents a merge, so suggesting mode cannot record one.
    if (editingMode === EditingMode.SUGGESTING) return;

    const plan = getCellMergePlan(editor);
    if (!plan) return;

    const {
      contentSourcePaths,
      continuationPaths,
      mergedCellPath,
      mergedCellProps,
      rect,
      removalPaths,
      tablePath,
    } = plan;

    AlineTransforms.withoutNormalizing(editor, () => {
      distributeCellContent(editor, {
        sourcePaths: contentSourcePaths,
        targetPaths: [mergedCellPath],
      });

      for (const cellPath of removalPaths.toReversed())
        Transforms.removeNodes(editor, { at: cellPath });

      for (const cellPath of continuationPaths)
        setMergedCellProps(editor, {
          at: cellPath,
          editingMode,
          props: { ...mergedCellProps, verticalMerge: 'continue' },
        });

      setMergedCellProps(editor, {
        at: mergedCellPath,
        editingMode,
        props: {
          ...mergedCellProps,
          verticalMerge: continuationPaths.length > 0 ? 'restart' : undefined,
        },
      });

      collapseUnusedGridColumns(editor, { rect, tablePath });
      collapseGhostRows(editor, { rect, tablePath });

      if (Node.has(editor, mergedCellPath)) AlineTransforms.select(editor, mergedCellPath);

      AlineTransforms.announce(editor, 'Merged table cells');
    });
  },

  /**
   * Update all table widths (table-level, column-level and cell-level) to match the current render
   * model cell widths.
   */
  normalizeTableWidths: (
    editor: Editor,
    options: {
      authorId: string;
      editingMode: EditingMode;
      path: Path;
      groupId?: string;
    }
  ): void => {
    const { authorId, editingMode, groupId, path } = options;

    const tableEntry = Editor.node(editor, path);
    if (!isTableEntry(tableEntry)) throw new Error('Expected table entry');
    const [table] = tableEntry;

    const [rMTable] = Queries.renderModelNodeFromCMLocation(editor, path);
    if (!isRenderModelTable(rMTable)) throw new Error('Expected render model table');

    const columnWidths = Queries.renderedTableColumnWidths(table, {
      availableWidth: pxToTwips(Queries.getContainerContentWidthPx(editor, { at: path })),
    });
    const gridColumns: Length.Twip[] = columnWidths.map(value => ({ type: 'tw', value }));

    AlineTransforms.withoutNormalizing(editor, () => {
      if (editingMode === EditingMode.SUGGESTING && !table.gridColumns) {
        Transforms.setNodes<TableElement>(editor, { gridColumns }, { at: path });
      }

      AlineTransforms.setTableProps(editor, {
        at: path,
        authorId,
        editingMode,
        groupId,
        props: {
          gridColumns,
          width: { type: 'tw', value: sum(columnWidths) },
        },
      });

      for (const [tableRowIndex, tableRow] of arrayEntries(table.children)) {
        for (const range of Queries.getTableRowCellRanges(tableRow)) {
          const { cellIndex, endColumnIndex, startColumnIndex, tableCell } = range;
          const tableCellPath = [...path, tableRowIndex, cellIndex];
          const cellWidth = sum(columnWidths.slice(startColumnIndex, endColumnIndex + 1));

          // When a cell is a suggested insertion, set the width directly
          // instead of creating a tableCellFormat suggestion. The width is
          // part of the insertion itself, not a separate change.
          const isCellInsertSuggestion =
            editingMode === EditingMode.SUGGESTING &&
            tableCell.suggestedEdits?.some(isInsertTableCellEdit);

          if (isCellInsertSuggestion) {
            Transforms.setNodes<TableCellElement>(
              editor,
              { width: { type: 'tw', value: cellWidth } },
              { at: tableCellPath }
            );
          } else {
            AlineTransforms.setTableCellProps(editor, {
              at: tableCellPath,
              authorId,
              editingMode,
              groupId,
              props: { width: { type: 'tw', value: cellWidth } },
            });
          }
        }
      }
    });
  },

  /**
   * Resizes the table columns by the given width delta in twips.
   * @returns The amount in twips by which the column edge was actually moved to the right.
   */
  resizeTableColumns(
    editor: Editor,
    options: {
      authorId: string;
      cMTablePath: Path;
      /**
       * The index of the column immediately to the right of the edge being dragged. If the
       * rightmost edge is being resized, this will equal the number of columns.
       */
      columnEdgeIndex: number;
      editingMode: EditingMode;
      /**
       * The amount in twips by which the column edge should be moved to the right.
       */
      widthDelta: number;
    }
  ): number {
    const { authorId, cMTablePath, columnEdgeIndex, editingMode, widthDelta } = options;

    return AlineTransforms.withoutNormalizing(editor, () => {
      // Ensure that all table widths are in twips before fetching any nodes
      AlineTransforms.normalizeTableWidths(editor, { authorId, editingMode, path: cMTablePath });

      const cMTableEntry = Editor.node(editor, cMTablePath);
      if (!isTableEntry(cMTableEntry)) throw new Error('Expected table entry');
      const [cMTable] = cMTableEntry;

      const columnCount = Queries.getTableColumnCount(cMTable);
      const leftColumnIndex = columnEdgeIndex > 0 ? columnEdgeIndex - 1 : null;
      const rightColumnIndex = columnEdgeIndex < columnCount ? columnEdgeIndex : null;

      const rMTablePath = Queries.getFirstRMPathFromCMPath(editor, cMTablePath);
      if (!rMTablePath) throw new Error('Expected render model path');

      const containerContentWidthPx = Queries.getRMContainerContentWidthPx(editor, {
        rMPath: rMTablePath,
      });
      const { leftMargin: leftMarginPx, rightMargin: rightMarginPx } =
        Queries.getRMContainerMargins(editor, rMTablePath, 'px');
      const containerWidth = pxToTwips(containerContentWidthPx + leftMarginPx + rightMarginPx);

      const leftBorderWidth = Queries.getTableMaximumBorderWidth(cMTable, 'left', 'tw');
      const rightBorderWidth = Queries.getTableMaximumBorderWidth(cMTable, 'right', 'tw');

      // Adjust available width to account for borders rendered outside the table,
      // preventing them from overflowing the container.
      const availableWidth = containerWidth - (leftBorderWidth + rightBorderWidth) / 2;
      const columnWidths = Queries.renderedTableColumnWidths(cMTable, {
        availableWidth,
      });
      const minimumColumnWidths = Queries.getTableMinimumColumnWidths(cMTable);

      const { width } = applySuggestions(cMTable);
      if (!width || typeof width === 'number' || width.type !== 'tw')
        throw new Error('Expected width to be in twips');

      const indent = Queries.renderedTableIndent(cMTable, 'tw');
      const indentForClamping = indent + pxToTwips(leftMarginPx);
      const clampedWidthDelta = getClampedWidthDelta({
        availableWidth,
        columnWidths,
        indent: indentForClamping,
        leftColumnIndex,
        minimumColumnWidths,
        rightColumnIndex,
        tableWidth: width.value,
        widthDelta,
      });

      const tableWidthChange =
        leftColumnIndex === null
          ? -clampedWidthDelta
          : rightColumnIndex === null
            ? clampedWidthDelta
            : 0;

      const resizedColumnWidths = columnWidths.map((columnWidth, columnIndex) => {
        if (columnIndex === leftColumnIndex) return columnWidth + clampedWidthDelta;
        if (columnIndex === rightColumnIndex) return columnWidth - clampedWidthDelta;
        return columnWidth;
      });

      AlineTransforms.setTableProps(editor, {
        at: cMTablePath,
        authorId,
        editingMode,
        props: {
          gridColumns: resizedColumnWidths.map(value => ({ type: 'tw', value })),
          width: { type: 'tw', value: width.value + tableWidthChange },
        },
      });

      function resizeCellsInColumn(columnIndex: number, direction: 'left' | 'right') {
        if (!isTableEntry(cMTableEntry)) throw new Error('Expected table entry');

        const signedWidthDelta = clampedWidthDelta * (direction === 'right' ? -1 : 1);

        for (const {
          endColumnIndex,
          startColumnIndex,
          tableCellEntry,
        } of Queries.getTableCellEntriesInColumn(cMTableEntry, columnIndex)) {
          // Do not resize cells that do not start/end at the column edge
          const startsOrEndsOnColumn = {
            left: endColumnIndex === columnIndex,
            right: startColumnIndex === columnIndex,
          }[direction];
          if (!startsOrEndsOnColumn) continue;

          const [tableCell, tableCellPath] = tableCellEntry;

          const { width } = applySuggestions(tableCell);
          if (!width || typeof width === 'number' || width.type !== 'tw')
            throw new Error('Expected width to be in twips');

          AlineTransforms.setTableCellProps(editor, {
            at: tableCellPath,
            authorId,
            editingMode,
            props: { width: { type: 'tw', value: width.value + signedWidthDelta } },
          });
        }
      }

      if (typeof leftColumnIndex === 'number') {
        resizeCellsInColumn(leftColumnIndex, 'left');
      } else if (clampedWidthDelta !== 0) {
        AlineTransforms.setTableProps(editor, {
          at: cMTablePath,
          authorId,
          editingMode,
          props: {
            indent: {
              type: 'tw',
              value: indent + clampedWidthDelta,
            },
          },
        });
      }

      if (typeof rightColumnIndex === 'number') {
        resizeCellsInColumn(rightColumnIndex, 'right');
      }

      return clampedWidthDelta;
    });
  },

  /**
   * Sets border properties on the selected table cells. When multiple cells are
   * selected, shared borders between adjacent cells are updated appropriately.
   * Only affects cells directly in the table at the given location (not nested
   * table cells).
   */
  setSelectedTableCellsBorder: (
    editor: Editor,
    options: {
      authorId: string;
      borderProps: Partial<TableBorder>;
      editingMode: EditingMode;
      at?: Location;
    }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { at = editor.selection, authorId, borderProps, editingMode } = options;
      if (!at) return;

      const tableEntry = Queries.atOrAbove(editor, { at, match: isTableElement });
      if (!tableEntry) return;

      const [, tablePath] = tableEntry;
      /**
       * With an active grid (table cell rectangle) selection, the affected cells are exactly the
       * rect's cells (including 'continue' ghost cells, which carry their own border props); the
       * linear anchor-to-focus range would also cover cells outside the rectangle.
       */
      const gridCellEntries = Queries.getGridSelectionInfoForLocation(editor, { at })
        ? Queries.getGridSelectionCellEntries(editor, { includeVerticalMergeContinuations: true })
        : null;
      const selectedCellEntries: NodeEntry<TableCellElement>[] =
        gridCellEntries ??
        Editor.nodes(editor, {
          at,
          match: isTableCellElement,
        })
          .filter(
            // Filter to avoid setting border props for parent cells, when modifying nested table cells
            ([, cellPath]) =>
              Path.isAncestor(tablePath, cellPath) && cellPath.length === tablePath.length + 2
          )
          .toArray();
      if (selectedCellEntries.length === 0) return;

      const borderUpdates = createBorderUpdates(editor, {
        borderProps,
        selectedCellEntries,
        tableEntry,
      });

      for (const { borders, path: cellPath } of borderUpdates) {
        const [cell] = Editor.node(editor, cellPath);
        if (!isTableCellElement(cell)) continue;

        const existingBorders = cell.borders ?? {};
        const mergedBorders: PartialTableBorders = { ...existingBorders };

        forEachObjIndexed((border, direction) => {
          mergedBorders[direction] = { ...existingBorders[direction], ...border };
        }, borders);

        const props: Partial<TableCellProps> = { borders: mergedBorders };

        switch (editingMode) {
          case EditingMode.EDITING: {
            AlineTransforms.removeConflictingSuggestions(editor, {
              editingMode,
              entry: [cell, cellPath],
              props,
            });
            Transforms.setNodes<TableCellElement>(editor, props, { at: cellPath });
            break;
          }
          case EditingMode.SUGGESTING: {
            const nextSuggestedEdits = Queries.newEdits(cell, {
              authorId,
              type: 'tableCellFormat',
              value: props,
            });
            if (!nextSuggestedEdits) break;

            Transforms.setNodes<TableCellElement>(
              editor,
              { suggestedEdits: nextSuggestedEdits },
              { at: cellPath }
            );
            break;
          }
        }
      }
    });
  },

  /**
   * Sets a table with new border properties.
   */
  setTableBorder: (
    editor: Editor,
    options: {
      authorId: string;
      borderProps: Partial<TableBorder>;
      editingMode: EditingMode;
      at?: Location;
    }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { at = editor.selection, authorId, borderProps, editingMode } = options;
      if (!at) return;

      const tableEntry = Queries.atOrAbove(editor, { at, match: isTableElement });
      if (!tableEntry) return;

      const [table, tablePath] = tableEntry;
      const currentBorders = Queries.mergeTableBordersWithDefaults(table);

      const newBorders: TableBorders = {
        bottom: { ...currentBorders.bottom, ...borderProps },
        insideH: { ...currentBorders.insideH, ...borderProps },
        insideV: { ...currentBorders.insideV, ...borderProps },
        left: { ...currentBorders.left, ...borderProps },
        right: { ...currentBorders.right, ...borderProps },
        top: { ...currentBorders.top, ...borderProps },
      };

      AlineTransforms.setTableProps(editor, {
        at: tablePath,
        authorId,
        editingMode,
        props: { borders: newBorders },
      });
    });
  },

  /**
   * Generic function to set any table cell props.
   */
  setTableCellProps: (
    editor: Editor,
    options: {
      authorId: string;
      editingMode: EditingMode;
      props: Partial<TableCellProps>;
      at?: Location;
      groupId?: string;
      match?: NodeMatch<TableCellElement>;
    }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { at = editor.selection, authorId, editingMode, groupId, match, props } = options;
      if (!at) return;

      /**
       * With an active grid (table cell rectangle) selection, the affected cells are exactly the
       * rect's cells; the linear anchor-to-focus range would also cover cells outside the rectangle.
       * Ghost 'continue' cells join via the vertical-merge group expansion below.
       */
      const gridCellEntries = Queries.getGridSelectionInfoForLocation(editor, { at })
        ? Queries.getGridSelectionCellEntries(editor)?.filter(
            ([cell, cellPath]) => !match || match(cell, cellPath)
          )
        : null;
      const tableCellEntries = (
        gridCellEntries ?? Array.from(Queries.getTableCellEntriesForLocation(editor, { at, match }))
      ).flatMap(cellEntry => Queries.getVerticallyMergedTableCellGroup(editor, cellEntry));

      if (!tableCellEntries.length) return;

      switch (editingMode) {
        case EditingMode.EDITING: {
          for (const [cell, cellPath] of tableCellEntries) {
            AlineTransforms.removeConflictingSuggestions(editor, {
              editingMode,
              entry: [cell, cellPath],
              props,
            });
            Transforms.setNodes<TableCellElement>(editor, props, {
              at: cellPath,
            });
          }
          return;
        }
        case EditingMode.SUGGESTING: {
          for (const [tableCell, tableCellPath] of tableCellEntries) {
            const nextSuggestedEdits = Queries.newEdits(tableCell, {
              authorId,
              groupId,
              type: 'tableCellFormat',
              value: props,
            });
            if (!nextSuggestedEdits) return;

            Transforms.setNodes<TableCellElement>(
              editor,
              { suggestedEdits: nextSuggestedEdits },
              { at: tableCellPath }
            );
          }
          return;
        }
      }
    });
  },

  /**
   * Generic function to set any table props.
   *
   * When setting `align` or `indent`, remember that:
   *
   * - When setting `indent` to a value, `align` must be set to `left`.
   * - When setting 'align' to anything other than `left`, indent must be set to { type: `nil` }
   */
  setTableProps: (
    editor: Editor,
    options: {
      authorId: string;
      editingMode: EditingMode;
      props: Partial<TableProps>;
      at?: Location;
      groupId?: string;
    }
  ): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { at = editor.selection, authorId, editingMode, groupId, props } = options;
      if (!at) return;

      const tableEntry = Queries.atOrAbove(editor, { at, match: isTableElement, mode: 'lowest' });
      if (!tableEntry) return;

      const [table, tablePath] = tableEntry;

      switch (editingMode) {
        case EditingMode.EDITING: {
          /**
           * Set the table's properties.
           */
          Transforms.setNodes<TableElement>(editor, props, { at: tablePath });
          /**
           * If we're modifying the fillColor of the table, Word's behavior is
           * to set the fillColor for each of the table's immediate children.
           */
          if (props.shade?.fillColor) {
            AlineTransforms.setTableCellProps(editor, {
              at: tablePath,
              authorId,
              editingMode,
              match: (node, path) => {
                /**
                 * Looking for the table cell that is two steps deeper than
                 * the table itself so that we don't overcapture cells of a
                 * nested table.
                 */
                return isTableCellElement(node) && path.length === tablePath.length + 2;
              },
              props: { shade: { fillColor: props.shade.fillColor } },
            });
          }
          return;
        }
        case EditingMode.SUGGESTING: {
          /**
           * Suggests the table properties.
           */
          const nextTableEdits = Queries.newEdits<TableElement>(table, {
            authorId,
            groupId,
            type: 'tableFormat',
            value: props,
          });
          if (!nextTableEdits) return;
          Transforms.setNodes<TableElement>(
            editor,
            { suggestedEdits: nextTableEdits },
            { at: tablePath }
          );
          if (props.shade?.fillColor) {
            AlineTransforms.setTableCellProps(editor, {
              at: tablePath,
              authorId,
              editingMode,
              groupId,
              match: (node, path) => {
                /**
                 * Looking for the table cell that is two steps deeper than
                 * the table itself so that we don't overcapture cells of a
                 * nested table.
                 */
                return isTableCellElement(node) && path.length === tablePath.length + 2;
              },
              props: { shade: { fillColor: props.shade.fillColor } },
            });
          }
          return;
        }
      }
    });
  },

  /**
   * Split the selected cells into `columnCount` columns and `rowCount` rows, dividing the selection
   * as a whole with `mergeBeforeSplit`, or each cell on its own without it.
   */
  splitTableCells: (
    editor: Editor,
    options: {
      authorId: string;
      columnCount: number;
      editingMode: EditingMode;
      mergeBeforeSplit: boolean;
      rowCount: number;
    }
  ): void => {
    const { authorId, columnCount, editingMode, mergeBeforeSplit, rowCount } = options;

    // No suggested edit type represents a split, so suggesting mode cannot record one.
    if (editingMode === EditingMode.SUGGESTING) return;

    const region = Queries.getSelectedGridRegion(editor);
    if (!region) return;

    const axes = getSplitAxes(region, { columnCount, mergeBeforeSplit, rowCount });
    if (!axes) return;

    const { rect } = region;
    const [, tablePath] = region.tableEntry;

    AlineTransforms.withoutNormalizing(editor, () => {
      // The column split reads gridColumns, which only normalizing guarantees.
      AlineTransforms.normalizeTableWidths(editor, { authorId, editingMode, path: tablePath });

      // Pool first, so rebuilding a row of merge ghosts cannot take visible content with it.
      if (mergeBeforeSplit) poolRegionContent(editor, { rect, tablePath });

      const splitOptions = { authorId, editingMode, tablePath };

      const columnCountAfter = axes.isSplittingColumns
        ? splitRegionColumns(editor, { ...splitOptions, columnCount, mergeBeforeSplit, rect })
        : Queries.getGridRectSize(rect).columnCount;

      const columnRect: TableGridRect = {
        ...rect,
        endColumnIndex: rect.startColumnIndex + columnCountAfter - 1,
      };

      const addedRowCount = axes.isSplittingRows
        ? splitRegionRows(editor, { ...splitOptions, rect: columnRect, rowCount })
        : 0;

      if (axes.isUnmergingRows) unmergeRegionRows(editor, { rect: columnRect, tablePath });

      const resultRect: TableGridRect = {
        ...columnRect,
        endRowIndex: columnRect.endRowIndex + addedRowCount,
      };

      if (mergeBeforeSplit) redistributeRegionContent(editor, { rect: resultRect, tablePath });

      selectRegion(editor, { rect: resultRect, tablePath });

      AlineTransforms.announce(editor, 'Split table cells');
    });
  },
};

/**
 * The nearest point outside the nodes at `paths`, stepping forward (or backward with `reverse`)
 * past each one the point is in. Undefined when the document ends before such a point.
 */
function getPointOutsideNodes(
  editor: Editor,
  point: Point,
  options: { paths: Path[]; reverse?: boolean }
): Point | undefined {
  const { paths, reverse = false } = options;

  let currentPoint: Point | undefined = point;
  while (currentPoint) {
    const { path: pointPath } = currentPoint;
    const containingPath = paths.find(path => Path.isAncestor(path, pointPath));
    if (!containingPath) return currentPoint;

    const nodeRange = Editor.range(editor, containingPath);
    currentPoint = reverse ? Editor.before(editor, nodeRange) : Editor.after(editor, nodeRange);
  }

  return undefined;
}

/** Plan a merge of the grid selection, or null when it does not close into a rect. */
function getCellMergePlan(editor: Editor): MergePlan | null {
  const gridSelectionInfo = Queries.getGridSelectionInfo(editor);
  if (!gridSelectionInfo) return null;

  const rect = Queries.getGridSelectionMergeRect(gridSelectionInfo);
  if (!rect) return null;

  const { tableEntry } = gridSelectionInfo;
  const matrix = Queries.getTableGridMatrix(tableEntry);

  const contentSourcePaths: Path[] = [];
  const continuationPaths: Path[] = [];
  const removalPaths: Path[] = [];
  let mergedCellPath: Path | null = null;

  for (const { columnIndex, isVerticalMergeContinuation, path, rowIndex } of getRegionCells(
    matrix,
    rect
  )) {
    // A ghost's content is never rendered, so moving it would surface text nobody has seen.
    if (!isVerticalMergeContinuation) contentSourcePaths.push(path);

    if (rowIndex === rect.startRowIndex && columnIndex === rect.startColumnIndex)
      mergedCellPath = path;
    else if (columnIndex === rect.startColumnIndex) continuationPaths.push(path);
    else removalPaths.push(path);
  }

  if (!mergedCellPath) return null;

  const width = getRegionWidth(matrix, rect);

  return {
    contentSourcePaths,
    continuationPaths,
    mergedCellPath,
    mergedCellProps: {
      columnSpan: Queries.getColumnSpanProp(Queries.getGridRectSize(rect).columnCount),
      ...(width ? { width } : {}),
    },
    rect,
    removalPaths,
    tablePath: tableEntry[1],
  };
}

/** A region's physical cells in reading order, each once however far it spans. */
function getRegionCells(matrix: (TableGridSlot | null)[][], rect: TableGridRect): RegionCell[] {
  const seenCells = new Set<TableCellElement>();
  const regionCells: RegionCell[] = [];

  for (let rowIndex = rect.startRowIndex; rowIndex <= rect.endRowIndex; rowIndex++) {
    for (
      let columnIndex = rect.startColumnIndex;
      columnIndex <= rect.endColumnIndex;
      columnIndex++
    ) {
      const slot = matrix[rowIndex]?.[columnIndex];
      if (!slot) continue;

      const [cell, path] = slot.physicalCellEntry;
      if (seenCells.has(cell)) continue;

      seenCells.add(cell);
      regionCells.push({
        columnIndex,
        isVerticalMergeContinuation: slot.isVerticalMergeContinuation,
        path,
        rowIndex,
      });
    }
  }

  return regionCells;
}

/**
 * The summed width of the region's first row, or undefined when a width is missing or not in
 * twips, in which case the merged cell keeps its own.
 */
function getRegionWidth(
  matrix: (TableGridSlot | null)[][],
  rect: TableGridRect
): Length.Twip | undefined {
  const seenCells = new Set<TableCellElement>();
  let totalTwips = 0;

  for (let columnIndex = rect.startColumnIndex; columnIndex <= rect.endColumnIndex; columnIndex++) {
    const slot = matrix[rect.startRowIndex]?.[columnIndex];
    if (!slot) return undefined;

    const [cell] = slot.physicalCellEntry;
    if (seenCells.has(cell)) continue;
    seenCells.add(cell);

    // A legacy width is a bare number of inches.
    const { width } = applySuggestions(cell);
    if (typeof width !== 'object' || width.type !== 'tw') return undefined;

    totalTwips += width.value;
  }

  return createTwips(totalTwips);
}

/** Set props on a merged cell, dropping any pending suggestions they overwrite. */
function setMergedCellProps(
  editor: Editor,
  options: { at: Path; editingMode: EditingMode; props: Partial<TableCellProps> }
): void {
  const { at, editingMode, props } = options;

  const cell = Node.getIf(editor, at);
  if (!cell || !isTableCellElement(cell)) return;

  AlineTransforms.removeConflictingSuggestions(editor, { editingMode, entry: [cell, at], props });
  Transforms.setNodes<TableCellElement>(editor, props, { at });
}

/**
 * Drop the grid columns a merge leaves without a cell edge, handing each one's width to its
 * neighbour. Column widths come from the cell that ends in each column, so a buried boundary would
 * otherwise fall through to the minimum width and widen the table.
 */
function collapseUnusedGridColumns(
  editor: Editor,
  options: { rect: TableGridRect; tablePath: Path }
): void {
  const { rect, tablePath } = options;

  const tableEntry = getTableEntry(editor, tablePath);
  if (!tableEntry) return;

  const [table] = tableEntry;
  const { gridColumns } = applySuggestions(table);
  if (!gridColumns) return;

  const usedEndColumnIndices = new Set(
    table.children.flatMap(row =>
      Queries.getTableRowCellRanges(row).map(range => range.endColumnIndex)
    )
  );

  const redundantColumnIndices: number[] = [];
  for (let columnIndex = rect.startColumnIndex; columnIndex < rect.endColumnIndex; columnIndex++)
    if (!usedEndColumnIndices.has(columnIndex)) redundantColumnIndices.push(columnIndex);

  if (redundantColumnIndices.length === 0) return;

  for (const [rowIndex, row] of arrayEntries(table.children).toReversed()) {
    for (const range of Queries.getTableRowCellRanges(row).toReversed()) {
      const removedColumnCount = redundantColumnIndices.filter(
        columnIndex => columnIndex >= range.startColumnIndex && columnIndex < range.endColumnIndex
      ).length;
      if (removedColumnCount === 0) continue;

      const columnSpan = Queries.renderedTableCellColumnSpan(range.tableCell) - removedColumnCount;

      Transforms.setNodes<TableCellElement>(
        editor,
        { columnSpan: Queries.getColumnSpanProp(columnSpan) },
        { at: [...tablePath, rowIndex, range.cellIndex] }
      );
    }
  }

  const keptEdgesTwips = getCumulativeEdges(gridColumns.map(({ value }) => value)).filter(
    (_, columnIndex) => !redundantColumnIndices.includes(columnIndex)
  );

  Transforms.setNodes<TableElement>(
    editor,
    { gridColumns: getWidthsBetweenEdges(keptEdgesTwips).map(createTwips) },
    { at: tablePath }
  );
}

/**
 * Drop the rows a merge leaves holding only empty ghosts, and end the merges above them, so merging
 * undoes the rows a row split added.
 */
function collapseGhostRows(
  editor: Editor,
  options: { rect: TableGridRect; tablePath: Path }
): void {
  const { rect, tablePath } = options;

  const tableEntry = getTableEntry(editor, tablePath);
  if (!tableEntry) return;

  const ghostRowIndices: number[] = [];
  for (let rowIndex = rect.startRowIndex + 1; rowIndex <= rect.endRowIndex; rowIndex++) {
    const row = tableEntry[0].children.at(rowIndex);
    if (!row || row.children.length === 0) continue;

    if (row.children.every(cell => getIsEmptyGhostCell(editor, cell)))
      ghostRowIndices.push(rowIndex);
  }

  if (ghostRowIndices.length === 0) return;

  for (const rowIndex of ghostRowIndices.toReversed())
    Transforms.removeNodes(editor, { at: [...tablePath, rowIndex] });

  const collapsedTableEntry = getTableEntry(editor, tablePath);
  if (!collapsedTableEntry) return;

  const matrix = Queries.getTableGridMatrix(collapsedTableEntry);
  const startRow = collapsedTableEntry[0].children.at(rect.startRowIndex);
  if (!startRow) return;

  for (const range of Queries.getTableRowCellRanges(startRow)) {
    if (Queries.renderedTableCellVerticalMerge(range.tableCell) !== 'restart') continue;

    const slotBelow = matrix[rect.startRowIndex + 1]?.[range.startColumnIndex];
    if (slotBelow?.isVerticalMergeContinuation) continue;

    Transforms.setNodes<TableCellElement>(
      editor,
      { verticalMerge: undefined },
      { at: [...tablePath, rect.startRowIndex, range.cellIndex] }
    );
  }
}

function getIsEmptyGhostCell(editor: Editor, tableCell: TableCellElement): boolean {
  if (Queries.renderedTableCellVerticalMerge(tableCell) !== 'continue') return false;

  // A cell a merge has just drained has no children until normalizing refills it.
  return tableCell.children.length === 0 || getIsEmptyTableCell(editor, tableCell);
}

/**
 * What the requested counts do to the selection, or null when nothing. Decided before any
 * mutation, so a no-op split leaves even the widths untouched.
 */
function getSplitAxes(
  region: { rect: TableGridRect; tableEntry: NodeEntry<TableElement> },
  options: { columnCount: number; mergeBeforeSplit: boolean; rowCount: number }
): {
  isSplittingColumns: boolean;
  isSplittingRows: boolean;
  isUnmergingRows: boolean;
} | null {
  const { columnCount, mergeBeforeSplit, rowCount } = options;
  const { rect, tableEntry } = region;
  const { columnCount: selectedColumnCount, rowCount: selectedRowCount } =
    Queries.getGridRectSize(rect);

  /** Merging first leaves the selection as it is for any count up to its current columns. */
  const isSplittingColumns = columnCount >= (mergeBeforeSplit ? selectedColumnCount + 1 : 2);

  /** Rows only divide a selection within one row. */
  const isSplittingRows = rowCount > 1 && selectedRowCount === 1;

  /** A vertically merged region unmerges back into its ghost rows, only for their exact count. */
  const isUnmergingRows =
    rowCount === selectedRowCount && Queries.getIsVerticallyMergedGridRect(tableEntry, rect);

  return isSplittingColumns || isSplittingRows || isUnmergingRows
    ? { isSplittingColumns, isSplittingRows, isUnmergingRows }
    : null;
}

/**
 * Divide the region into `columnCount` columns and return how many it spans afterwards. The grid
 * gains the split's edges, and rows outside the region widen their spans to keep their widths.
 */
function splitRegionColumns(
  editor: Editor,
  options: {
    authorId: string;
    columnCount: number;
    editingMode: EditingMode;
    mergeBeforeSplit: boolean;
    rect: TableGridRect;
    tablePath: Path;
  }
): number {
  const { authorId, columnCount, editingMode, mergeBeforeSplit, rect, tablePath } = options;

  const tableEntry = getTableEntry(editor, tablePath);
  if (!tableEntry) throw new Error('Expected table');

  const [table] = tableEntry;
  const { gridColumns } = applySuggestions(table);
  if (!gridColumns) throw new Error('Expected gridColumns');

  // Whole twips, since a fractional width (from a legacy inch width) cannot be divided evenly.
  const regionWidthsTwips = gridColumns
    .slice(rect.startColumnIndex, rect.endColumnIndex + 1)
    .map(({ value }) => Math.round(value));
  const regionEdgesTwips = getCumulativeEdges(regionWidthsTwips);
  const edgeOptions = { columnCount, rect, regionEdgesTwips };

  // Merged first, the region divides as a whole; otherwise each of its cells divides on its own.
  const splitRanges = mergeBeforeSplit
    ? [rect]
    : table.children
        .slice(rect.startRowIndex, rect.endRowIndex + 1)
        .flatMap(row =>
          Queries.getTableRowCellRanges(row).filter(range => getIsRangeWithinRect(range, rect))
        );
  const splitEdgesTwips = splitRanges.flatMap(range => getRangeSplitEdges(range, edgeOptions));
  const gridEdgesTwips = [...new Set([...regionEdgesTwips, ...splitEdgesTwips])].toSorted(
    (a, b) => a - b
  );

  const partOptions = { ...edgeOptions, gridEdgesTwips };
  const columnSpans = getSpansBetweenEdges(regionEdgesTwips, gridEdgesTwips);

  // Reverse order keeps the paths ahead valid while cells are inserted.
  for (const [rowIndex, row] of arrayEntries(table.children).toReversed()) {
    const rowPath = [...tablePath, rowIndex];
    const cellRanges = Queries.getTableRowCellRanges(row);

    if (rowIndex < rect.startRowIndex || rowIndex > rect.endRowIndex) {
      for (const range of cellRanges.toReversed()) {
        const addedColumnCount = getAddedColumnCount(range, { columnSpans, rect });
        if (addedColumnCount === 0) continue;

        AlineTransforms.setTableCellProps(editor, {
          at: [...rowPath, range.cellIndex],
          authorId,
          editingMode,
          props: {
            columnSpan: Queries.renderedTableCellColumnSpan(range.tableCell) + addedColumnCount,
          },
        });
      }

      continue;
    }

    const regionRanges = cellRanges.filter(range => getIsRangeWithinRect(range, rect));

    if (mergeBeforeSplit) {
      replaceRowCellsWithParts(editor, {
        authorId,
        editingMode,
        parts: getRangeParts(rect, partOptions),
        ranges: regionRanges,
        rowPath,
      });
    } else {
      for (const range of regionRanges.toReversed())
        divideCellIntoParts(editor, {
          authorId,
          editingMode,
          parts: getRangeParts(range, partOptions),
          range,
          rowPath,
        });
    }
  }

  AlineTransforms.setTableProps(editor, {
    at: tablePath,
    authorId,
    editingMode,
    props: {
      gridColumns: [
        ...gridColumns.slice(0, rect.startColumnIndex),
        ...getWidthsBetweenEdges(gridEdgesTwips).map(createTwips),
        ...gridColumns.slice(rect.endColumnIndex + 1),
      ],
    },
  });

  AlineTransforms.normalizeTableWidths(editor, { authorId, editingMode, path: tablePath });

  return gridEdgesTwips.length;
}

/** The parts a column range divides into, each spanning the grid columns between its edges. */
function getRangeParts(
  range: Pick<TableGridRect, 'endColumnIndex' | 'startColumnIndex'>,
  options: {
    columnCount: number;
    gridEdgesTwips: number[];
    rect: TableGridRect;
    regionEdgesTwips: number[];
  }
): RegionPart[] {
  const { gridEdgesTwips } = options;

  const { startTwips } = getRangeExtentTwips(range, options);
  const partEdgesTwips = getRangeSplitEdges(range, options);
  const columnSpans = getSpansBetweenEdges(partEdgesTwips, gridEdgesTwips, startTwips);
  const widthsTwips = getWidthsBetweenEdges(partEdgesTwips, startTwips);

  return [...zip(columnSpans, widthsTwips)].map(([columnSpan, widthTwips]) => ({
    columnSpan,
    width: createTwips(widthTwips),
  }));
}

/** Where a column range's parts end, dividing its own width into `columnCount`. */
function getRangeSplitEdges(
  range: Pick<TableGridRect, 'endColumnIndex' | 'startColumnIndex'>,
  options: { columnCount: number; rect: TableGridRect; regionEdgesTwips: number[] }
): number[] {
  const { columnCount } = options;

  const { endTwips, startTwips } = getRangeExtentTwips(range, options);

  return getCumulativeEdges(getFrontLoadedCounts(endTwips - startTwips, columnCount)).map(
    edgeTwips => edgeTwips + startTwips
  );
}

function getRangeExtentTwips(
  range: Pick<TableGridRect, 'endColumnIndex' | 'startColumnIndex'>,
  options: { rect: TableGridRect; regionEdgesTwips: number[] }
): { endTwips: number; startTwips: number } {
  const { rect, regionEdgesTwips } = options;

  return {
    endTwips: regionEdgesTwips[range.endColumnIndex - rect.startColumnIndex] ?? 0,
    startTwips: regionEdgesTwips[range.startColumnIndex - rect.startColumnIndex - 1] ?? 0,
  };
}

/** How many grid columns the split adds to a cell outside the region. */
function getAddedColumnCount(
  range: TableRowCellRange,
  options: { columnSpans: number[]; rect: TableGridRect }
): number {
  const { columnSpans, rect } = options;

  const firstColumnIndex = Math.max(range.startColumnIndex, rect.startColumnIndex);
  const lastColumnIndex = Math.min(range.endColumnIndex, rect.endColumnIndex);
  if (lastColumnIndex < firstColumnIndex) return 0;

  const overlappedSpans = columnSpans.slice(
    firstColumnIndex - rect.startColumnIndex,
    lastColumnIndex - rect.startColumnIndex + 1
  );

  return sum(overlappedSpans) - overlappedSpans.length;
}

/** Replace a row's region cells with the parts, pooling their content into the first part. */
function replaceRowCellsWithParts(
  editor: Editor,
  options: {
    authorId: string;
    editingMode: EditingMode;
    parts: RegionPart[];
    ranges: TableRowCellRange[];
    rowPath: Path;
  }
): void {
  const { authorId, editingMode, parts, ranges, rowPath } = options;

  const firstRange = ranges.at(0);
  const lastRange = ranges.at(-1);
  if (!firstRange || !lastRange || parts.length === 0) return;

  const template = getTemplateCellProps(firstRange.tableCell, rowPath, firstRange.cellIndex, {
    copyVerticalMerge: true,
  });
  const insertionIndex = lastRange.cellIndex + 1;

  // Insert after the old cells so their content can move across before they are removed.
  Transforms.insertNodes(editor, createPartCells(parts, template, { authorId, editingMode }), {
    at: [...rowPath, insertionIndex],
  });

  distributeCellContent(editor, {
    sourcePaths: ranges.map(range => [...rowPath, range.cellIndex]),
    targetPaths: [[...rowPath, insertionIndex]],
  });

  for (let cellIndex = lastRange.cellIndex; cellIndex >= firstRange.cellIndex; cellIndex--)
    Transforms.removeNodes(editor, { at: [...rowPath, cellIndex] });
}

/** Divide one cell into the parts, sharing its content across them. */
function divideCellIntoParts(
  editor: Editor,
  options: {
    authorId: string;
    editingMode: EditingMode;
    parts: RegionPart[];
    range: TableRowCellRange;
    rowPath: Path;
  }
): void {
  const { authorId, editingMode, parts, range, rowPath } = options;

  const [firstPart, ...remainingParts] = parts;
  if (!firstPart || remainingParts.length === 0) return;

  const { cellIndex, tableCell } = range;
  const cellPath = [...rowPath, cellIndex];
  const template = getTemplateCellProps(tableCell, rowPath, cellIndex, {
    copyVerticalMerge: true,
  });

  AlineTransforms.setTableCellProps(editor, {
    at: cellPath,
    authorId,
    editingMode,
    props: getPartCellProps(firstPart),
  });

  Transforms.insertNodes(
    editor,
    createPartCells(remainingParts, template, { authorId, editingMode }),
    { at: Path.next(cellPath) }
  );

  distributeCellContent(editor, {
    sourcePaths: [cellPath],
    targetPaths: parts.map((_, index) => [...rowPath, cellIndex + index]),
  });
}

function createPartCells(
  parts: RegionPart[],
  template: TemplateCellResult,
  options: { authorId: string; editingMode: EditingMode }
): TableCellElement[] {
  return parts.map(part => ({
    ...createInsertedCell(part.width, template, options),
    ...getPartCellProps(part),
  }));
}

/** Every part states its width, since an auto width resolves to its last grid column alone. */
function getPartCellProps({ columnSpan, width }: RegionPart): Partial<TableCellProps> {
  return { columnSpan: Queries.getColumnSpanProp(columnSpan), width };
}

/**
 * Divide a single-row region into `rowCount` rows and return how many were added. Its cells become
 * the first new row; the row's other cells start vertical merges across the new ones.
 */
function splitRegionRows(
  editor: Editor,
  options: {
    authorId: string;
    editingMode: EditingMode;
    rect: TableGridRect;
    rowCount: number;
    tablePath: Path;
  }
): number {
  const { authorId, editingMode, rect, rowCount, tablePath } = options;

  const tableEntry = getTableEntry(editor, tablePath);
  if (!tableEntry) return 0;

  const regionRow = tableEntry[0].children.at(rect.startRowIndex);
  if (!regionRow) return 0;

  const rowPath = [...tablePath, rect.startRowIndex];
  const cellRanges = Queries.getTableRowCellRanges(regionRow);

  for (const range of cellRanges.toReversed()) {
    if (getIsRangeWithinRect(range, rect)) continue;
    // A cell already in a merge is extended by the ghosts below it.
    if (Queries.renderedTableCellVerticalMerge(range.tableCell) !== undefined) continue;

    AlineTransforms.setTableCellProps(editor, {
      at: [...rowPath, range.cellIndex],
      authorId,
      editingMode,
      props: { verticalMerge: 'restart' },
    });
  }

  Transforms.insertNodes(
    editor,
    createGhostRows(rowCount - 1, cellRanges, { authorId, editingMode, rect, rowPath }),
    { at: Path.next(rowPath) }
  );

  return rowCount - 1;
}

/** Rows with a cell per range: new cells inside the region, merge ghosts outside it. */
function createGhostRows(
  count: number,
  templateRanges: TableRowCellRange[],
  options: { authorId: string; editingMode: EditingMode; rect: TableGridRect; rowPath: Path }
): TableRowElement[] {
  const { authorId, editingMode, rect, rowPath } = options;

  return Array.from({ length: count }, () => ({
    ...createDefaultTableRowElement({ columnCount: templateRanges.length }),
    children: templateRanges.map((range): TableCellElement => {
      const { cellIndex, tableCell } = range;
      const template = getTemplateCellProps(tableCell, rowPath, cellIndex, {
        copyVerticalMerge: false,
      });
      return {
        ...createInsertedCell(tableCell.width, template, { authorId, editingMode }),
        columnSpan: Queries.getColumnSpanProp(Queries.renderedTableCellColumnSpan(tableCell)),
        verticalMerge: getIsRangeWithinRect(range, rect) ? undefined : 'continue',
      };
    }),
  }));
}

/** Clear the vertical merges in a region, so its existing ghost cells stand as cells again. */
function unmergeRegionRows(
  editor: Editor,
  options: { rect: TableGridRect; tablePath: Path }
): void {
  const { rect, tablePath } = options;

  const tableEntry = getTableEntry(editor, tablePath);
  if (!tableEntry) return;

  for (const { path } of getRegionCells(Queries.getTableGridMatrix(tableEntry), rect))
    Transforms.setNodes<TableCellElement>(editor, { verticalMerge: undefined }, { at: path });
}

/** Pool the region's visible content into its first visible cell. */
function poolRegionContent(
  editor: Editor,
  options: { rect: TableGridRect; tablePath: Path }
): void {
  const cellPaths = getVisibleRegionCellPaths(editor, options);

  distributeCellContent(editor, { sourcePaths: cellPaths, targetPaths: cellPaths.slice(0, 1) });
}

/** Pool the region's visible content and deal it back out across its visible cells in reading order. */
function redistributeRegionContent(
  editor: Editor,
  options: { rect: TableGridRect; tablePath: Path }
): void {
  const cellPaths = getVisibleRegionCellPaths(editor, options);

  distributeCellContent(editor, { sourcePaths: cellPaths, targetPaths: cellPaths });
}

/** A merge ghost's content is never rendered, so content neither comes from nor goes to one. */
function getVisibleRegionCellPaths(
  editor: Editor,
  options: { rect: TableGridRect; tablePath: Path }
): Path[] {
  const { rect, tablePath } = options;

  const tableEntry = getTableEntry(editor, tablePath);
  if (!tableEntry) return [];

  return getRegionCells(Queries.getTableGridMatrix(tableEntry), rect)
    .filter(({ isVerticalMergeContinuation }) => !isVerticalMergeContinuation)
    .map(({ path }) => path);
}

/** Select a whole region, which reads back as a grid selection over its cells. */
function selectRegion(editor: Editor, options: { rect: TableGridRect; tablePath: Path }): void {
  const { rect, tablePath } = options;

  const tableEntry = getTableEntry(editor, tablePath);
  if (!tableEntry) return;

  const matrix = Queries.getTableGridMatrix(tableEntry);
  const firstCellPath = matrix[rect.startRowIndex]?.[rect.startColumnIndex]?.physicalCellEntry[1];
  const lastCellPath = matrix[rect.endRowIndex]?.[rect.endColumnIndex]?.physicalCellEntry[1];
  if (!firstCellPath || !lastCellPath) return;

  AlineTransforms.select(editor, {
    anchor: Editor.start(editor, firstCellPath),
    focus: Editor.end(editor, lastCellPath),
  });
}

/** Pool the source cells' content into the first target, then share it across all targets. */
function distributeCellContent(
  editor: Editor,
  options: { sourcePaths: Path[]; targetPaths: Path[] }
): void {
  const { sourcePaths, targetPaths } = options;

  const [poolPath] = targetPaths;
  if (!poolPath) return;

  for (const sourcePath of sourcePaths) {
    if (Path.equals(sourcePath, poolPath)) continue;

    const sourceCell = Node.getIf(editor, sourcePath);
    if (!sourceCell || !isTableCellElement(sourceCell)) continue;
    if (getIsEmptyTableCell(editor, sourceCell)) continue;

    moveCellBlocks(editor, {
      count: sourceCell.children.length,
      fromIndex: 0,
      fromPath: sourcePath,
      toPath: poolPath,
    });
  }

  if (targetPaths.length === 1) return;

  const poolCell = Node.getIf(editor, poolPath);
  if (!poolCell || !isTableCellElement(poolCell)) return;

  const [keptBlockCount = 0, ...movedBlockCounts] = getFrontLoadedCounts(
    poolCell.children.length,
    targetPaths.length
  );

  for (const [toPath, count] of zip(targetPaths.slice(1), movedBlockCounts))
    moveCellBlocks(editor, { count, fromIndex: keptBlockCount, fromPath: poolPath, toPath });
}

/**
 * Move blocks to the end of another cell, replacing its empty placeholder paragraph. Each move
 * shifts the rest down, so every block is taken from `fromIndex`.
 */
function moveCellBlocks(
  editor: Editor,
  options: { count: number; fromIndex: number; fromPath: Path; toPath: Path }
): void {
  const { count, fromIndex, fromPath, toPath } = options;
  if (count === 0) return;

  const toCell = Node.getIf(editor, toPath);
  if (!toCell || !isTableCellElement(toCell)) return;

  const hadPlaceholder = getIsEmptyTableCell(editor, toCell);
  const toIndex = toCell.children.length;

  for (let movedCount = 0; movedCount < count; movedCount++) {
    Transforms.moveNodes(editor, {
      at: [...fromPath, fromIndex],
      to: [...toPath, toIndex + movedCount],
    });
  }

  if (hadPlaceholder) Transforms.removeNodes(editor, { at: [...toPath, 0] });
}

/** Whether a cell holds only the empty paragraph it was created with. */
function getIsEmptyTableCell(editor: Editor, tableCell: TableCellElement): boolean {
  const [firstBlock] = tableCell.children;

  return (
    tableCell.children.length === 1 &&
    isParagraphElement(firstBlock) &&
    Editor.isEmpty(editor, firstBlock)
  );
}

function getIsRangeWithinRect(range: TableRowCellRange, rect: TableGridRect): boolean {
  return (
    range.startColumnIndex >= rect.startColumnIndex && range.endColumnIndex <= rect.endColumnIndex
  );
}

function getTableEntry(editor: Editor, tablePath: Path): NodeEntry<TableElement> | null {
  const table = Node.getIf(editor, tablePath);

  return table && isTableElement(table) ? [table, tablePath] : null;
}

function createTwips(value: number): Length.Twip {
  return { type: 'tw', value };
}

interface BorderUpdate {
  borders: PartialTableBorders;
  path: Path;
}

function createBorderUpdates(
  editor: Editor,
  options: {
    borderProps: Partial<TableBorder>;
    selectedCellEntries: NodeEntry<TableCellElement>[];
    tableEntry: NodeEntry<TableElement>;
  }
) {
  const { borderProps, selectedCellEntries, tableEntry } = options;
  const [table, tablePath] = tableEntry;

  const borderUpdates = new Map<string, BorderUpdate>();

  for (const cellEntry of selectedCellEntries) {
    addBorderUpdatesForCell(cellEntry);
  }

  return Array.from(borderUpdates.values());

  function addBorderUpdate(cellPath: Path, direction: 'top' | 'bottom' | 'left' | 'right') {
    const key = cellPath.join(',');
    const existing = borderUpdates.get(key);
    const existingBorders = existing?.borders ?? {};
    const mergedBorders = {
      ...existingBorders,
      [direction]: { ...existingBorders[direction], ...borderProps },
    };
    borderUpdates.set(key, { borders: mergedBorders, path: cellPath });
  }

  function addBorderUpdatesForCell(cellEntry: NodeEntry<TableCellElement>) {
    const vmGroup = Queries.getVerticallyMergedTableCellGroup(editor, cellEntry);

    for (let i = 0; i < vmGroup.length; i++) {
      const [, vmCellPath] = vmGroup[i];
      const vmRowIndex = vmCellPath[tablePath.length];
      const vmCellIndex = vmCellPath[tablePath.length + 1];
      const vmCellEntry = vmGroup[i];
      const startCol = Queries.getTableCellStartColumn(editor, vmCellEntry);
      const endCol = Queries.getTableCellEndColumn(editor, vmCellEntry);

      addBorderUpdate(vmCellPath, 'top');
      addBorderUpdate(vmCellPath, 'bottom');
      addBorderUpdate(vmCellPath, 'left');
      addBorderUpdate(vmCellPath, 'right');

      const vmRow = table.children[vmRowIndex];

      const leftNeighbor = vmRow.children[vmCellIndex - 1];
      const rightNeighbor = vmRow.children[vmCellIndex + 1];

      if (leftNeighbor) addBorderUpdate([...tablePath, vmRowIndex, vmCellIndex - 1], 'right');
      if (rightNeighbor) addBorderUpdate([...tablePath, vmRowIndex, vmCellIndex + 1], 'left');

      if (i === 0) {
        for (const [, abovePath] of Queries.getTableCellEntriesInRowByColumnRange(
          tableEntry,
          vmRowIndex - 1,
          { endColumnIndex: endCol, startColumnIndex: startCol }
        )) {
          addBorderUpdate(abovePath, 'bottom');
        }
      }

      if (i === vmGroup.length - 1) {
        for (const [, belowPath] of Queries.getTableCellEntriesInRowByColumnRange(
          tableEntry,
          vmRowIndex + 1,
          { endColumnIndex: endCol, startColumnIndex: startCol }
        )) {
          addBorderUpdate(belowPath, 'top');
        }
      }
    }
  }
}

/**
 * Returns a width delta adjusted so affected columns never go below their minimum widths and
 * the table never goes beyond the page edges (container content width + left margin + right margin).
 */
function getClampedWidthDelta(options: {
  availableWidth: number;
  columnWidths: number[];
  indent: number;
  leftColumnIndex: number | null;
  minimumColumnWidths: number[];
  rightColumnIndex: number | null;
  tableWidth: number;
  widthDelta: number;
}): number {
  const {
    availableWidth,
    columnWidths,
    indent,
    leftColumnIndex,
    minimumColumnWidths,
    rightColumnIndex,
    tableWidth,
    widthDelta,
  } = options;

  if (widthDelta === 0) return 0;

  const isRightEdge = rightColumnIndex === null;
  const isLeftEdge = leftColumnIndex === null;

  let clampedWidthDelta = widthDelta;

  if (widthDelta > 0 && !isRightEdge) {
    const currentWidth = columnWidths[rightColumnIndex] ?? 0;
    const minimumWidth = minimumColumnWidths[rightColumnIndex] ?? 0;
    const maxDelta = currentWidth - minimumWidth;
    if (maxDelta <= 0) return 0;
    clampedWidthDelta = Math.min(widthDelta, maxDelta);
  }

  if (widthDelta < 0 && !isLeftEdge) {
    const currentWidth = columnWidths[leftColumnIndex] ?? 0;
    const minimumWidth = minimumColumnWidths[leftColumnIndex] ?? 0;
    const maxDelta = currentWidth - minimumWidth;
    if (maxDelta <= 0) return 0;
    clampedWidthDelta = Math.max(widthDelta, -maxDelta);
  }

  if (isLeftEdge && clampedWidthDelta < 0) {
    return Math.max(clampedWidthDelta, -indent);
  }

  if (isRightEdge && clampedWidthDelta > 0) {
    const maxTableWidth = availableWidth - indent;
    const maxIncrease = maxTableWidth - tableWidth;
    if (maxIncrease <= 0) return 0;
    return Math.min(clampedWidthDelta, maxIncrease);
  }

  return clampedWidthDelta;
}

export default TableTransforms;
